import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _axios from 'axios';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PortalAuthContext } from './portalAuth.context';
import portalAxios, {
  setActivePortalSlug,
  setPortalAuthFailureHandler,
} from '../../api/portalAxios';
import {
  clearPortalTokens,
  getPortalTokens,
  setPortalTenantId,
  setPortalTokens,
} from '../../api/portalUtils';
import {
  getPortalLookupEndpoint,
  PORTAL_ME_ENDPOINT,
  PORTAL_REQUEST_CODE_ENDPOINT,
  PORTAL_TOKEN_ENDPOINT,
} from '../../consts';
import { PortalContact, PortalLookupResponse } from '../../types/portal';

export const PortalAuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { slug = '' } = useParams();
  const { t } = useTranslation();

  // undefined = still resolving, null = no such portal (or it is switched off)
  const [portal, setPortal] = useState<PortalLookupResponse | null | undefined>(undefined);
  const [contact, setContact] = useState<PortalContact | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Point the shared instance at this portal before any request goes out.
  setActivePortalSlug(slug);

  const signOut = useCallback(() => {
    clearPortalTokens(slug);
    setContact(null);
  }, [slug]);

  useEffect(() => {
    setPortalAuthFailureHandler(() => setContact(null));
    return () => setPortalAuthFailureHandler(null);
  }, []);

  // Resolve the slug to a tenant. This one call is deliberately made on a bare
  // axios instance: it runs in the public schema, before any tenant is known.
  useEffect(() => {
    let cancelled = false;

    const lookup = async () => {
      if (!slug) {
        setPortal(null);
        return;
      }

      try {
        const response = await _axios.get<PortalLookupResponse>(
          `${process.env.REACT_APP_API_ADDRESS}${getPortalLookupEndpoint(slug)}`
        );
        if (cancelled) return;

        setPortalTenantId(slug, response.data.tenant_id);
        setPortal(response.data);
      } catch {
        if (!cancelled) setPortal(null);
      }
    };

    lookup();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Once the tenant is known, pick up any session already stored for this slug.
  useEffect(() => {
    let cancelled = false;

    const loadContact = async () => {
      if (!portal) {
        if (portal === null) setContact(null);
        return;
      }

      if (!getPortalTokens(slug).access) {
        setContact(null);
        return;
      }

      try {
        const response = await portalAxios.get<PortalContact>(PORTAL_ME_ENDPOINT);
        if (!cancelled) setContact(response.data);
      } catch {
        if (!cancelled) {
          clearPortalTokens(slug);
          setContact(null);
        }
      }
    };

    loadContact();
    return () => {
      cancelled = true;
    };
  }, [portal, slug]);

  const requestCode = useCallback(
    async (email: string) => {
      setLoading(true);
      setError(null);
      try {
        await portalAxios.post(PORTAL_REQUEST_CODE_ENDPOINT, { email });
        return true;
      } catch {
        setError(t('We could not send the code. Please try again in a moment.'));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const signIn = useCallback(
    async (email: string, code: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await portalAxios.post(PORTAL_TOKEN_ENDPOINT, { email, code });
        setPortalTokens(slug, response.data);

        const me = await portalAxios.get<PortalContact>(PORTAL_ME_ENDPOINT);
        setContact(me.data);
        return true;
      } catch {
        setError(t('The code is invalid or has expired.'));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [slug, t]
  );

  const hasModule = useCallback(
    (module: string) => Boolean(portal?.modules?.includes(module as never)),
    [portal]
  );

  const value = useMemo(
    () => ({
      slug,
      portal,
      contact,
      isSignedIn: Boolean(contact),
      loading,
      error,
      requestCode,
      signIn,
      signOut,
      hasModule,
    }),
    [slug, portal, contact, loading, error, requestCode, signIn, signOut, hasModule]
  );

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
};
